import * as React from 'react';
import { Fragment, useState, useEffect, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { cn } from '../../lib/utils';

export default function ModelListFolderOptionMenu({ folder, reloadState, setCurrentFolder, anchorOrigin, transformOrigin, anchorEl = undefined as HTMLElement | null | undefined, onClose = undefined as (() => void) | undefined }) {
    // Support both new props (anchorEl/onClose) and legacy folder.anchorEl
    const effectiveAnchorEl = anchorEl !== undefined ? anchorEl : folder.anchorEl;
    const menuOpen = Boolean(effectiveAnchorEl);
    const closeMenu = onClose || (() => { folder.anchorEl = null; });
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (menuOpen && effectiveAnchorEl) {
            const rect = effectiveAnchorEl.getBoundingClientRect();
            const menuWidth = 200;
            const menuHeight = 200; // approximate
            setMenuPos({
                top: Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 8),
                left: Math.min(rect.left, window.innerWidth - menuWidth - 8),
            });
        } else {
            setMenuPos(null);
        }
    }, [menuOpen]);

    const handleClose = () => {
        closeMenu();
    };

    return (
        <Menu as="div" className="relative inline-block">
            {menuOpen && menuPos && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={handleClose}
                    />
                    <Transition
                        show={!!menuOpen}
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Menu.Items
                            ref={menuRef}
                            static
                            className="z-20 w-[200px] rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none overflow-hidden"
                            style={{
                                position: 'fixed',
                                top: menuPos.top,
                                left: menuPos.left,
                            }}
                        >
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={() => {
                                            closeMenu();
                                            setCurrentFolder(folder);
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left',
                                            active && 'bg-black/5'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">folder_open</span>
                                        Open
                                    </button>
                                )}
                            </Menu.Item>

                            <div className="border-t border-slate-100" />

                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={() => {
                                            folder.editing = true;
                                            closeMenu();
                                            reloadState();
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left',
                                            active && 'bg-black/5'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2 -ml-1">drive_file_rename_outline</span>
                                        Rename
                                    </button>
                                )}
                            </Menu.Item>

                            <div className="border-t border-slate-100" />

                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={() => {
                                            folder.deleteModalOpen = true;
                                            closeMenu();
                                            reloadState();
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left',
                                            active && 'bg-black/5'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2 -ml-1 text-red-600">delete_forever</span>
                                        <span className="text-red-600">Delete</span>
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );
}
